import React, { Component } from 'react';
import PropTypes from 'prop-types';
import autoBind from 'react-autobind';
import { injectIntl, defineMessages, intlShape } from 'react-intl';
import classnames from 'classnames';

import Skill from 'ui/common/rebranding/components/Skill';
import AddSkillModal from 'ui/users/skills/add/modal';
import AddSkillModalTrigger from 'ui/users/skills/add/modal-trigger';

import { skillLevelIds, skillLevels } from 'resources/skills/levels';
import SkillDetailModal from 'ui/graphql/SkillDetailModal';
import { UPDATE_SKILL_NAME } from 'store/graphql/SkillDetailModal/action-types';
import shortid from 'shortid';
import { show as showModal } from 'redux-modal';
import { connect } from 'react-redux';
import { startCaseHandleSpecialChars } from 'utils/string';

const messages = defineMessages({
  add: {
    id: 'users.skills.add-button.title',
    defaultMessage: 'Add New Skill',
  },
  validationCountsBySkillLevelIdCount: {
    id: 'users.skills.validationCountsBySkillLevelIdCount',
    defaultMessage: `{count, number} {levelName, select,
      Beginner {beginner}
      Intermediate {intermediate}
      Expert {expert}
    } {count, plural,
      one {validation}
      other {validations}
    }`,
  },
  noValidation: {
    id: 'users.skills.no-validations',
    defaultMessage: 'UNVALIDATED',
  },
});

class UserSkillsGrid extends Component {
  static propTypes = {
    entries: PropTypes.arrayOf(
      PropTypes.shape({
        skill: PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          category: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            className: PropTypes.string.isRequired,
          }),
        }),
        level: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
        validations: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
            level: PropTypes.string.isRequired,
          })
        ),
        validationCountsBySkillLevelIds: PropTypes.shape({
          [skillLevelIds.beginner]: PropTypes.number,
          [skillLevelIds.intermediate]: PropTypes.number,
          [skillLevelIds.expert]: PropTypes.number,
        }),
        isValidatedAtCurrentLevel: PropTypes.bool,
      })
    ),
    showAddButton: PropTypes.bool,
    hasPointerForEntry: PropTypes.func,
    onEntryClick: PropTypes.func,
    entryColClass: PropTypes.string,
    isHighlighted: PropTypes.bool,
    intl: intlShape,
    canManage: PropTypes.bool,
  };

  static defaultProps = {
    entryColClass: 'col-6 col-md-3 col-lg-2',
  };

  constructor(props) {
    super(props);

    autoBind(this);

    // very important. This helps new engineers to import multiple skill grids without
    // fear of breaking multiple skillDetailModal sharing same MODAL_NAME
    this.state = {
      random: shortid.generate(),
      name: false,
      isModalOpen: false,
    };
  }

  renderAddSkillButton() {
    const { entryColClass, isHighlighted } = this.props;

    return (
      <div key="add" className={classnames(entryColClass, 'py-3')}>
        <AddSkillModal />
        <AddSkillModalTrigger isHighlighted={isHighlighted} />
      </div>
    );
  }

  actualClick(entry) {
    this.props.updateName(entry.skill.name);
    this.setState({
      isModalOpen: true,
    });
  }

  closeModalFn() {
    this.setState({
      isModalOpen: false,
    });
  }

  renderEntry(entry) {
    const { canManage, entryColClass, intl: { formatMessage } } = this.props;
    if (entry.isDeleted) return null; // Skill is deleted, not show on user side

    return (
      <div key={shortid.generate()} className={classnames(entryColClass, 'pt-3')}>
        <div>
          <Skill
            id={`user_skill_${entry.id}`}
            isPointer={canManage}
            onClick={() => {
              if (canManage) {
                this.actualClick(entry);
              }
            }}
            categoryClasses={
              entry.skill.category ? `${entry.skill.category.className} gradient` : ''
            }
            skillLevel={entry.level.power}
            fixed
            onHover
            validated={entry.isValidatedAtCurrentLevel}
          >
            <small className="pb-2">
              <b>{entry && entry.skill && startCaseHandleSpecialChars(entry.skill.name)}</b>
            </small>

            {entry.validationCountsBySkillLevelIds[skillLevelIds.beginner] !== 0 ||
            entry.validationCountsBySkillLevelIds[skillLevelIds.intermediate] !== 0 ||
            entry.validationCountsBySkillLevelIds[skillLevelIds.expert] !== 0 ? (
              Object.keys(entry.validationCountsBySkillLevelIds)
                .map(skillLevelId => {
                  const level = skillLevels[skillLevelId];
                  return entry.validationCountsBySkillLevelIds[skillLevelId] ? (
                    <div key={skillLevelId}>
                      <small>
                        {formatMessage(messages.validationCountsBySkillLevelIdCount, {
                          count: entry.validationCountsBySkillLevelIds[skillLevelId].toString(),
                          levelName: level.name,
                        })}
                      </small>
                    </div>
                  ) : null;
                })
                .filter(Boolean)
            ) : (
              <div>
                <small>{formatMessage(messages.noValidation)}</small>
              </div>
            )}
          </Skill>
        </div>
      </div>
    );
  }

  render() {
    const { entries, showAddButton, className } = this.props;
    const { isModalOpen } = this.state;

    return (
      entries && (
        <div className={`row ${className}`}>
          <SkillDetailModal isOpen={isModalOpen} closeFn={() => this.closeModalFn()} />
          {entries.map(this.renderEntry)}
          {showAddButton && this.renderAddSkillButton()}
        </div>
      )
    );
  }
}

const mapDispatchToProps = function(dispatch) {
  return {
    showModal: modalName => dispatch(showModal(modalName)),
    updateName: name =>
      dispatch({
        type: UPDATE_SKILL_NAME,
        payload: { name },
      }),
  };
};

export default connect(null, mapDispatchToProps)(injectIntl(UserSkillsGrid));
